import { useCallback, useEffect, useState } from "react";
import { deleteSsoProvider, getOrgSso, patchOrgSso, type AdminSsoStatus } from "../lib/api.ts";
import { toast } from "./Toast.tsx";

/**
 * Admin view of an org's SSO: the capability gate (Enterprise plan or a per-org
 * override staff can flip for a one-off deal), the "Require SSO" toggle, and the
 * org's registered providers (with verification status + remove). Registration
 * itself is self-service (org owner) via the native endpoints.
 */
export function OrgSsoSection({ orgId }: { orgId: string }) {
  const [sso, setSso] = useState<AdminSsoStatus | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setSso(await getOrgSso(orgId));
    } catch {
      /* best-effort */
    }
  }, [orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function patch(body: { ssoEnabled?: boolean; ssoEnforced?: boolean }) {
    setBusy(true);
    try {
      await patchOrgSso(orgId, body);
      await load();
      toast("SSO settings updated.", true);
    } catch {
      toast("Couldn't update SSO.");
    } finally {
      setBusy(false);
    }
  }

  async function removeProvider(providerId: string) {
    try {
      await deleteSsoProvider(orgId, providerId);
      await load();
      toast("Provider removed.", true);
    } catch {
      toast("Couldn't remove the provider.");
    }
  }

  if (!sso) return null;

  return (
    <section className="drawer-section">
      <h4>Single Sign-On</h4>
      <p className="hint">
        SSO is {sso.enabled ? "available" : "unavailable"} for this org (plan:{" "}
        <strong>{sso.plan}</strong>
        {sso.ssoEnabledOverride ? ", admin override on" : ""}).
      </p>
      <div className="custom-grid">
        <label className="field" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <input
            type="checkbox"
            checked={sso.ssoEnabledOverride}
            disabled={busy}
            onChange={(e) => void patch({ ssoEnabled: e.target.checked })}
          />
          <span>Grant SSO (capability override)</span>
        </label>
        <label className="field" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <input
            type="checkbox"
            checked={sso.enforced}
            disabled={busy}
            onChange={(e) => void patch({ ssoEnforced: e.target.checked })}
          />
          <span>Require SSO</span>
        </label>
      </div>
      {sso.providers.length > 0 && (
        <table className="data" style={{ marginTop: 10 }}>
          <thead>
            <tr><th>Domain</th><th>Status</th><th /></tr>
          </thead>
          <tbody>
            {sso.providers.map((p) => (
              <tr key={p.id}>
                <td className="mono">{p.domain}</td>
                <td>{p.domainVerified ? "Verified" : "Unverified"}</td>
                <td className="col-right">
                  <button className="btn btn-sm" type="button" onClick={() => void removeProvider(p.providerId)}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
